import React from 'react';
import { Button } from './button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface AppleCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  getEventsForDate: (date: Date) => Array<{
    id: string;
    title: string;
    time: string;
    status: 'confirmed' | 'scheduled' | 'cancelled' | 'pending';
    color?: string;
    hasConflict?: boolean;
    isCurrentUser?: boolean;
  }>;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onEventClick?: (event: {
    id: string;
    title: string;
    time: string;
    status: 'confirmed' | 'scheduled' | 'cancelled' | 'pending';
    color?: string;
  }) => void;
  onShowMoreClick?: (date: Date) => void;
}

export const AppleCalendar: React.FC<AppleCalendarProps> = ({
  selectedDate,
  onDateSelect,
  getEventsForDate,
  onPreviousMonth,
  onNextMonth,
  onToday,
  onEventClick,
  onShowMoreClick,
}) => {
  const renderCalendarDays = () => {
    const firstDayOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0
    );
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

    const days = [];
    const totalDays = 42; // 6 weeks * 7 days

    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const events = getEventsForDate(date);

      days.push(
        <button
          key={i}
          onClick={() => onDateSelect(date)}
          className={`relative h-20 p-1 text-left hover:bg-gray-50 transition-colors ${
            isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
          } ${isToday ? 'bg-blue-50' : ''} ${
            isSelected ? 'bg-blue-100 ring-2 ring-blue-500' : ''
          }`}
        >
          <div className="flex flex-col h-full min-h-0">
            <div
              className={`text-sm font-medium flex-shrink-0 ${
                isToday ? 'text-blue-600' : ''
              }`}
            >
              {date.getDate()}
            </div>

            {/* Event indicators */}
            <div className="flex-1 flex flex-col gap-0.5 mt-1 min-h-0 overflow-hidden">
              <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                {events.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 flex-shrink-0 ${
                      event.hasConflict
                        ? 'bg-red-100 text-red-700 border-2 border-red-500 font-semibold'
                        : event.isCurrentUser
                        ? 'bg-green-100 text-green-700 border border-green-500 font-medium ring-1 ring-green-200'
                        : event.status === 'confirmed'
                        ? 'bg-green-50 text-green-700'
                        : event.status === 'scheduled'
                        ? 'bg-blue-50 text-blue-700'
                        : event.status === 'cancelled'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-gray-50 text-gray-700'
                    }`}
                    title={
                      event.hasConflict
                        ? `⚠️ Conflict: ${event.title} - ${event.time}`
                        : `${event.title} - ${event.time}${event.isCurrentUser ? ' (You)' : ''}`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick?.(event);
                    }}
                  >
                    {event.hasConflict && '⚠️ '}
                    {event.time}
                  </div>
                ))}
              </div>

              {events.length > 3 && (
                <button
                  type="button"
                  className="text-xs text-gray-500 font-medium hover:text-gray-700 hover:underline flex-shrink-0 text-left w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Call onShowMoreClick if provided, otherwise do nothing
                    if (onShowMoreClick) {
                      onShowMoreClick(date);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  title={`Click to see all ${events.length} events for this day`}
                >
                  +{events.length - 3} more
                </button>
              )}
            </div>
          </div>
        </button>
      );
    }

    return days;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPreviousMonth}
            className="h-8 w-8 p-0 hover:bg-gray-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onNextMonth}
            className="h-8 w-8 p-0 hover:bg-gray-100"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
            })}
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onToday}
          className="text-sm"
        >
          Today
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="p-2">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px mb-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div
              key={`day-header-${index}`}
              className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-px">{renderCalendarDays()}</div>
      </div>
    </div>
  );
};
